import { useEffect, useLayoutEffect, useRef, useState, useMemo, useId } from 'react'
import * as d3 from 'd3'
import dayjs from 'dayjs'
import Styles from './FollowerGrowthGraph.module.scss'
import { useExternalIntegrationsWithMetrics } from 'hooks/queries/externalIntegrations/useExternalIntegrationsWithMetrics'
import { ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import { getMetricSnapshot } from 'screens/blaze/Home/components/Analytics/utils'
import { extractProfileInfo } from 'components/ExternalIntegrationSelect/utils'
import { InstagramBrand } from 'blaze-ui/icons/24'
import { FacebookBrand } from 'blaze-ui/icons/35'
import { TacticIntegration } from 'components/TacticIntegrationList/TacticIntegration/TacticIntegration'
import Silhouette from 'components/Previews/InstagramPreview/silhouette.jpg'
import { StyledIntegrationButton } from './StyledIntegrationButton'
import { InitiateOauthTrigger } from 'components/ConnectIntegrationTrigger/InitiateOauthTrigger'
import { useConnectAccountPopup } from 'components/Onboarding/useConnectAccountPopup'
import { useOnboardingTracking } from 'hooks/useOnboardingTracking'

interface DataPoint {
  date: Date
  followers: number
  isProjected: boolean
  isMilestone?: boolean
  baselineFollowers?: number
}

const CONFIG = {
  defaults: {
    followerCount: 1435,
    fallbackForZero: 100,
  },
  growth: {
    blue: { exponent: 2.0, targetMultiplier: 14.0, startMultiplier: 0.9 },
    gray: { exponent: 1.5, targetMultiplier: 2.5, startMultiplier: 0.85 },
  },
  timeline: {
    historicalMonths: 2,
    projectionMonths: 6,
    extensionWeeksBefore: 2,
    extensionWeeksAfter: 8,
    dataInterval: 7,
  },
  dimensions: {
    height: 400,
    margin: { top: 35, right: 0, bottom: 55, left: 30 },
    yAxisCompressionFactor: 0.75,
  },
  visual: {
    blueLineColor: '#2FA8FF',
    grayLineColor: '#E2E7EB',
    blueLineWidth: 4,
    grayLineWidth: 3.5,
    dotRadius: 4,
    dotStrokeWidth: 2.5,
    hoverAreaRadius: 25,
    milestoneLabel: ['First week of', 'content posted'],
    projectedPointRatios: [0.2, 0.4, 0.6, 0.8],
    tooltipOffsetY: 160,
    focusLineTopOffset: 6,
    focusLineBottomOffset: 135,
  },
} as const

const calculateExponentialGrowth = (progress: number, exponent: number, multiplier: number, baseValue: number) => {
  const exponentialGrowth = Math.pow(progress, exponent)
  return Math.round(baseValue * (1 + (multiplier - 1) * exponentialGrowth))
}

const generateTimeSeriesData = (
  startDate: Date,
  daysCount: number,
  interval: number,
  calculateFollowers: (dayIndex: number) => number,
  isProjected: boolean | ((dayIndex: number) => boolean),
  milestoneDay?: number,
): DataPoint[] => {
  const data: DataPoint[] = []
  for (let i = 0; i <= daysCount; i += interval) {
    const isMilestone = milestoneDay !== undefined && Math.abs(i - milestoneDay) < interval
    data.push({
      date: dayjs(startDate).add(i, 'days').toDate(),
      followers: calculateFollowers(i),
      isProjected: typeof isProjected === 'function' ? isProjected(i) : isProjected,
      isMilestone,
    })
  }

  return data
}

const getTimelineValues = () => {
  const today = new Date()
  const { historicalMonths, projectionMonths, extensionWeeksBefore, extensionWeeksAfter } = CONFIG.timeline
  const historicalDays = historicalMonths * 30
  const extensionDays = extensionWeeksBefore * 7
  const projectionDays = projectionMonths * 30 + extensionWeeksAfter * 7
  const todayIndex = extensionDays + historicalDays
  const totalDays = extensionDays + historicalDays + projectionDays
  const startDate = dayjs(today).subtract(historicalMonths, 'months').subtract(extensionWeeksBefore, 'weeks').toDate()

  return { today, startDate, projectionDays, todayIndex, totalDays }
}

const generateLineData = (
  config: { exponent: number; targetMultiplier: number },
  todayValue: number,
  startValue: number,
  milestoneDay?: number,
): DataPoint[] => {
  const { startDate, todayIndex, totalDays, projectionDays } = getTimelineValues()

  return generateTimeSeriesData(
    startDate,
    totalDays,
    CONFIG.timeline.dataInterval,
    (i) => {
      if (i < todayIndex) {
        const progress = i / todayIndex
        return Math.round(startValue + (todayValue - startValue) * progress)
      }
      const projectedProgress = (i - todayIndex + CONFIG.timeline.dataInterval) / projectionDays
      return calculateExponentialGrowth(projectedProgress, config.exponent, config.targetMultiplier, todayValue)
    },
    (i: number) => i >= todayIndex,
    milestoneDay,
  )
}

const generateBlueLineData = (currentFollowers?: number): DataPoint[] => {
  const todayValue = currentFollowers || CONFIG.defaults.followerCount
  const startValue = Math.round(todayValue * CONFIG.growth.blue.startMultiplier)
  return generateLineData(CONFIG.growth.blue, todayValue, startValue, getTimelineValues().todayIndex)
}

const generateGrayLineData = (blueData: DataPoint[]): DataPoint[] => {
  const todayValue = blueData.find((d) => d.isMilestone)?.followers || CONFIG.defaults.followerCount
  const startValue = Math.round(todayValue * CONFIG.growth.gray.startMultiplier)
  return generateLineData(CONFIG.growth.gray, todayValue, startValue, getTimelineValues().todayIndex)
}

const createLineGenerator = (xScale: d3.ScaleTime<number, number>, yScale: d3.ScaleLinear<number, number>) =>
  d3
    .line<DataPoint>()
    .x((d) => xScale(d.date))
    .y((d) => yScale(d.followers))
    .curve(d3.curveMonotoneX)

const createGradient = (svg: d3.Selection<SVGSVGElement, unknown, null, undefined>) => {
  const defs = svg.select('defs').empty() ? svg.append('defs') : svg.select('defs')

  if (defs.select('#projected-gradient').empty()) {
    const gradient = defs
      .append('linearGradient')
      .attr('id', 'projected-gradient')
      .attr('x1', '0%')
      .attr('y1', '0%')
      .attr('x2', '0%')
      .attr('y2', '100%')

    gradient
      .append('stop')
      .attr('offset', '0%')
      .attr('stop-color', CONFIG.visual.blueLineColor)
      .attr('stop-opacity', 0.3)
    gradient
      .append('stop')
      .attr('offset', '100%')
      .attr('stop-color', CONFIG.visual.blueLineColor)
      .attr('stop-opacity', 0.05)
  }

  return defs
}

const createDataPointCircles = (
  g: d3.Selection<SVGGElement, unknown, null, undefined>,
  className: string,
  data: DataPoint[],
  xScale: d3.ScaleTime<number, number>,
  yScale: d3.ScaleLinear<number, number>,
  color: string,
) => {
  g.selectAll(`.${className}`)
    .data(data)
    .enter()
    .append('circle')
    .attr('class', className)
    .attr('cx', (d) => xScale(d.date))
    .attr('cy', (d) => yScale(d.followers))
    .attr('r', CONFIG.visual.dotRadius)
    .attr('fill', '#fff')
    .attr('stroke', color)
    .attr('stroke-width', CONFIG.visual.dotStrokeWidth)
    .attr('filter', 'drop-shadow(0px 2px 10px rgba(0, 0, 0, 0.06))')
    .style('pointer-events', 'none')
}

const findNearestPoint = (points: DataPoint[], targetDate: Date): DataPoint | undefined => {
  return points.find((p) => Math.abs(p.date.getTime() - targetDate.getTime()) < 1000 * 60 * 60 * 24)
}

interface FollowerGrowthGraphProps {
  onIntegrationStatusChange?: (hasIntegration: boolean) => void
}

export const FollowerGrowthGraph = ({ onIntegrationStatusChange }: FollowerGrowthGraphProps = {}) => {
  const svgRef = useRef<SVGSVGElement | null>(null)
  const wrapperRef = useRef<HTMLDivElement | null>(null)
  const gRef = useRef<d3.Selection<SVGGElement, unknown, null, undefined> | null>(null)
  const [tooltipData, setTooltipData] = useState<DataPoint | null>(null)
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 })
  const [showHoverTooltip, setShowHoverTooltip] = useState(false)
  const [selectedIntegrationId, setSelectedIntegrationId] = useState<number | null>(null)
  const [previousIntegrationCount, setPreviousIntegrationCount] = useState(0)
  const clipPathId = `graph-clip-${useId()}`

  const { data: integrationsData, refetch } = useExternalIntegrationsWithMetrics()
  const { connect: originalConnect } = useConnectAccountPopup()
  const { trackIntegrationConnected } = useOnboardingTracking()

  const handleIntegrationConnect = () => {
    refetch()
  }

  const connect = (popup?: Window, options?: any) => {
    const wrappedOptions = {
      ...options,
      onConnect: (integrations: any[]) => {
        integrations.forEach((integration) => {
          trackIntegrationConnected(integration.provider, true)
        })
        options?.onConnect?.(integrations)
        refetch()
      },
    }
    return originalConnect(popup, wrappedOptions)
  }

  const availableIntegrations = useMemo(() => {
    if (!integrationsData?.external_integrations) {
      return []
    }

    const filtered = integrationsData.external_integrations.filter(
      (integration) =>
        (integration.provider === ExternalIntegrationEnum.Instagram ||
          integration.provider === ExternalIntegrationEnum.Facebook) &&
        integration.latest_metric_snapshots &&
        integration.latest_metric_snapshots.length > 0,
    )

    // Sort by ID (creation order) - earlier integrations first
    return filtered.sort((a, b) => a.id - b.id)
  }, [integrationsData])

  useEffect(() => {
    if (availableIntegrations.length > 0) {
      const currentIsValid = availableIntegrations.some((int) => int.id === selectedIntegrationId)

      const newIntegrationAdded = availableIntegrations.length > previousIntegrationCount

      if (!currentIsValid || newIntegrationAdded) {
        const latestIntegration = availableIntegrations.reduce((latest, current) =>
          current.id > latest.id ? current : latest,
        )
        setSelectedIntegrationId(latestIntegration.id)
      }

      if (availableIntegrations.length !== previousIntegrationCount) {
        setPreviousIntegrationCount(availableIntegrations.length)
      }
    } else {
      setSelectedIntegrationId(null)
      setPreviousIntegrationCount(0)
    }
  }, [availableIntegrations, selectedIntegrationId, previousIntegrationCount])

  const primaryIntegration = useMemo(() => {
    if (!selectedIntegrationId || availableIntegrations.length === 0) {
      return null
    }
    return availableIntegrations.find((int) => int.id === selectedIntegrationId) || null
  }, [selectedIntegrationId, availableIntegrations])

  useEffect(() => {
    onIntegrationStatusChange?.(!!primaryIntegration)
  }, [primaryIntegration, onIntegrationStatusChange])

  useLayoutEffect(() => {
    setTooltipData(null)
    setShowHoverTooltip(false)
  }, [primaryIntegration])

  useEffect(() => {
    if (!primaryIntegration && gRef.current && svgRef.current) {
      const svg = d3.select(svgRef.current as SVGSVGElement)
      svg.selectAll('*').remove()
      gRef.current = null
    }
  }, [primaryIntegration])

  const currentFollowers = useMemo(() => {
    if (!primaryIntegration) {
      return undefined
    }

    const followerSnapshot = getMetricSnapshot(primaryIntegration, 'followers_count')

    if (!followerSnapshot) {
      return undefined
    }

    const followerCount = followerSnapshot.metric_value
    if (followerCount === 0) {
      return CONFIG.defaults.fallbackForZero
    }

    return followerCount
  }, [primaryIntegration])

  const profileInfo = useMemo(() => {
    if (!primaryIntegration) {
      return null
    }

    const info = extractProfileInfo(primaryIntegration)

    return {
      avatar: info.avatar || Silhouette,
      name: info.name,
      provider: primaryIntegration.provider,
    }
  }, [primaryIntegration])

  const blueData = useMemo(() => {
    return generateBlueLineData(currentFollowers)
  }, [currentFollowers])

  const grayData = useMemo(() => {
    return generateGrayLineData(blueData)
  }, [blueData])

  useEffect(() => {
    if (
      !svgRef.current ||
      !wrapperRef.current ||
      !blueData.length ||
      !grayData.length ||
      !blueData.find((d) => d.isMilestone) ||
      !grayData.find((d) => d.isMilestone)
    ) {
      return
    }

    const wrapperWidth = wrapperRef.current?.clientWidth || 1100
    const { margin, height } = CONFIG.dimensions
    const innerWidth = wrapperWidth - margin.left - margin.right
    const innerHeight = height - margin.top - margin.bottom

    const svg = d3.select(svgRef.current as SVGSVGElement)

    if (!gRef.current) {
      svg.selectAll('*').remove()
      svg.attr('width', wrapperWidth).attr('height', height).style('overflow', 'visible')
      const g = svg.append('g').attr('transform', `translate(${margin.left},${margin.top})`)
      gRef.current = g
    } else {
      svg.attr('width', wrapperWidth).attr('height', height)
      gRef.current.attr('transform', `translate(${margin.left},${margin.top})`)
    }

    const g = gRef.current
    g.selectAll('*').remove()

    const [minDate, maxDate] = d3.extent(blueData, (d) => d.date) as [Date, Date]
    const xScale = d3.scaleTime().domain([minDate, maxDate]).range([0, innerWidth])
    const maxFollowers = d3.max(blueData, (d) => d.followers) as number
    const yScale = d3
      .scaleLinear()
      .domain([0, maxFollowers * CONFIG.dimensions.yAxisCompressionFactor])
      .range([innerHeight, 0])

    const defs = createGradient(svg)
    defs
      .append('clipPath')
      .attr('id', clipPathId)
      .append('rect')
      .attr('x', -margin.left)
      .attr('y', -margin.top)
      .attr('width', innerWidth + margin.left)
      .attr('height', innerHeight + margin.top)

    const actualData = blueData.filter((d) => !d.isProjected)
    const projectedData = blueData.filter((d) => d.isProjected)
    const transitionPoint = actualData.find((d) => d.isMilestone) || actualData[actualData.length - 1]
    const projectedWithTransition = [transitionPoint, ...projectedData]
    const grayProjectedPoints = grayData.filter((d) => d.date >= transitionPoint.date)

    const lineGenerator = createLineGenerator(xScale, yScale)

    const gapAreaGenerator = d3
      .area<{ date: Date; upper: number; lower: number }>()
      .x((d) => xScale(d.date))
      .y0((d) => yScale(d.lower))
      .y1((d) => yScale(d.upper))
      .curve(d3.curveMonotoneX)

    const gapData = projectedWithTransition.map((projPoint) => {
      const basePoint = findNearestPoint(grayProjectedPoints, projPoint.date)
      return {
        date: projPoint.date,
        upper: projPoint.followers,
        lower: basePoint?.followers || projPoint.followers,
      }
    })

    g.append('path').datum(gapData).attr('fill', 'url(#projected-gradient)').attr('d', gapAreaGenerator)

    const clippedGroup = g.append('g').attr('clip-path', `url(#${clipPathId})`)

    clippedGroup
      .append('path')
      .datum(grayData)
      .attr('fill', 'none')
      .attr('stroke', CONFIG.visual.grayLineColor)
      .attr('stroke-width', CONFIG.visual.grayLineWidth)
      .attr('d', lineGenerator)

    clippedGroup
      .append('path')
      .datum(projectedWithTransition)
      .attr('fill', 'none')
      .attr('stroke', CONFIG.visual.blueLineColor)
      .attr('stroke-width', CONFIG.visual.blueLineWidth)
      .attr('d', lineGenerator)

    const focusGroup = g.append('g').style('opacity', 0)
    focusGroup
      .append('line')
      .attr('class', 'focus-line')
      .attr('stroke', 'rgba(0, 0, 0, 0.2)')
      .attr('stroke-dasharray', '3,3')
    focusGroup
      .append('circle')
      .attr('r', CONFIG.visual.dotRadius)
      .attr('fill', '#fff')
      .attr('stroke', CONFIG.visual.grayLineColor)
      .attr('stroke-width', CONFIG.visual.dotStrokeWidth)
      .attr('filter', 'drop-shadow(0px 2px 10px rgba(0, 0, 0, 0.06))')
    focusGroup
      .append('circle')
      .attr('r', CONFIG.visual.dotRadius)
      .attr('fill', '#fff')
      .attr('stroke', CONFIG.visual.blueLineColor)
      .attr('stroke-width', CONFIG.visual.dotStrokeWidth)
      .attr('filter', 'drop-shadow(0px 2px 10px rgba(0, 0, 0, 0.06))')
    focusGroup.append('line').attr('stroke', 'rgba(0, 0, 0, 0.15)').attr('stroke-dasharray', '2,2')

    const projectedVisiblePoints = CONFIG.visual.projectedPointRatios
      .map((ratio) => projectedData[Math.floor(projectedData.length * ratio)])
      .filter(Boolean)

    createDataPointCircles(g, 'today-point', [transitionPoint], xScale, yScale, CONFIG.visual.blueLineColor)
    createDataPointCircles(g, 'visible-point', projectedVisiblePoints, xScale, yScale, CONFIG.visual.blueLineColor)

    g.selectAll('.hover-area')
      .data(projectedVisiblePoints)
      .enter()
      .append('circle')
      .attr('cx', (d) => xScale(d.date))
      .attr('cy', (d) => yScale(d.followers))
      .attr('r', CONFIG.visual.hoverAreaRadius)
      .attr('fill', 'transparent')
      .style('cursor', 'pointer')
      .on('mouseenter', (event, d) => {
        const baselinePoint = findNearestPoint(grayData, d.date)
        if (!baselinePoint) return

        const cx = xScale(d.date)
        const cyBlue = yScale(d.followers)
        const cyGray = yScale(baselinePoint.followers)

        focusGroup.style('opacity', 1)
        focusGroup.select('.focus-line').attr('x1', cx).attr('x2', cx).attr('y1', cyGray).attr('y2', cyBlue)
        focusGroup.selectAll('circle').attr('cx', cx)
        focusGroup.select('circle:nth-child(2)').attr('cy', cyGray)
        focusGroup.select('circle:nth-child(3)').attr('cy', cyBlue)
        focusGroup
          .select('line:nth-child(4)')
          .attr('x1', cx)
          .attr('x2', cx)
          .attr('y1', cyBlue + CONFIG.visual.focusLineTopOffset)
          .attr('y2', cyBlue - CONFIG.visual.focusLineBottomOffset)

        setTooltipData({ ...d, baselineFollowers: baselinePoint.followers })
        setTooltipPosition({ x: cx + margin.left, y: cyBlue + margin.top - CONFIG.visual.tooltipOffsetY })
        setShowHoverTooltip(true)
      })
      .on('mouseleave', () => {
        focusGroup.style('opacity', 0)
        setShowHoverTooltip(false)
      })

    const todayX = xScale(transitionPoint.date)
    const todayY = yScale(transitionPoint.followers)
    const [line1, line2] = CONFIG.visual.milestoneLabel
    const textWidth = Math.max(line1.length, line2.length) * 5.5 + 28

    g.append('line')
      .attr('x1', todayX)
      .attr('x2', todayX)
      .attr('y1', todayY - 5)
      .attr('y2', todayY - 44)
      .attr('stroke', '#BDBDBD')

    const labelGroup = g.append('g').attr('transform', `translate(${todayX},${todayY - 62})`)
    labelGroup
      .append('rect')
      .attr('x', -textWidth / 2)
      .attr('y', -18)
      .attr('width', textWidth)
      .attr('height', 46)
      .attr('fill', 'var(--light-60)')
      .attr('rx', 11)
      .attr('stroke', 'rgba(189, 189, 189, 0.5)')
    ;[line1, line2].forEach((text, i) => {
      labelGroup
        .append('text')
        .attr('text-anchor', 'middle')
        .attr('y', i * 14)
        .attr('fill', 'var(--dark-60)')
        .style('font-size', '12px')
        .style('font-weight', '500')
        .style('font-family', 'Sohne, sans-serif')
        .text(text)
    })

    const today = new Date()
    const { historicalMonths } = CONFIG.timeline
    const twoMonthsAgo = dayjs(today).subtract(historicalMonths, 'months').toDate()
    const axisY = innerHeight + 28

    g.append('line')
      .attr('x1', -margin.left)
      .attr('x2', innerWidth)
      .attr('y1', axisY - 18)
      .attr('y2', axisY - 18)
      .attr('stroke', '#EDEDED')

    const timelineLabels = [
      { label: '2 months ago', x: xScale(twoMonthsAgo) - 15, align: 'start' },
      { label: 'Today', x: xScale(today), align: 'middle' },
      { label: 'in 6 months', x: innerWidth - 16, align: 'end' },
    ]

    timelineLabels.forEach(({ label, x, align }) => {
      g.append('text')
        .attr('x', x)
        .attr('y', axisY)
        .attr('text-anchor', align as any)
        .attr('fill', 'var(--dark-80)')
        .style('font-size', '14px')
        .style('font-family', 'Sohne, sans-serif')
        .text(label)
    })
  }, [blueData, grayData, clipPathId])

  const { beforeValue, projectedFutureValue, beforeDelta, projectedDelta, afterValue } = useMemo(() => {
    const actualData = blueData.filter((d) => !d.isProjected)
    const todayPoint = actualData.find((d) => d.isMilestone) || actualData[actualData.length - 1]
    const todayValue = todayPoint?.followers || CONFIG.defaults.followerCount

    const beforeValue = tooltipData?.baselineFollowers || todayValue
    const projectedFutureValue = tooltipData?.followers || todayValue

    const beforeDelta = Math.round(((beforeValue - todayValue) / todayValue) * 100)
    const projectedDelta = Math.round(((projectedFutureValue - todayValue) / todayValue) * 100)

    return { beforeValue, projectedFutureValue, beforeDelta, projectedDelta, afterValue: todayValue }
  }, [blueData, tooltipData])

  const hasIntegrationWithoutMetrics = integrationsData?.external_integrations?.some(
    (integration) =>
      (integration.provider === ExternalIntegrationEnum.Instagram ||
        integration.provider === ExternalIntegrationEnum.Facebook) &&
      (!integration.latest_metric_snapshots || integration.latest_metric_snapshots.length === 0),
  )

  if (!primaryIntegration) {
    return (
      <div className={Styles.preIntegrationContainer}>
        <div className={Styles.integrationButtonsContainer}>
          {hasIntegrationWithoutMetrics ? (
            <>
              <h2 className={Styles.integrationHeader}>Loading your growth projections...</h2>
              <div className={Styles.loadingSubtext}>This may take a few moments</div>
            </>
          ) : (
            <>
              <h2 className={Styles.integrationHeader}>Connect to see growth projections:</h2>
              <div className={Styles.styledButtonsContainer}>
                <InitiateOauthTrigger provider={ExternalIntegrationEnum.Instagram} popUp onInitiatedOauth={connect}>
                  <StyledIntegrationButton provider="instagram" />
                </InitiateOauthTrigger>
                <InitiateOauthTrigger provider={ExternalIntegrationEnum.Facebook} popUp onInitiatedOauth={connect}>
                  <StyledIntegrationButton provider="facebook" />
                </InitiateOauthTrigger>
              </div>
            </>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className={Styles.container}>
      <div className={Styles.graphWrapper} ref={wrapperRef}>
        <svg ref={svgRef} />

        <div className={Styles.statsCard}>
          <div className={Styles.statsHeader}>
            {availableIntegrations.length > 1 ? (
              <div className={Styles.integrationSwitcher}>
                {availableIntegrations.map((integration) => {
                  const info = extractProfileInfo(integration)
                  const isSelected = integration.id === selectedIntegrationId
                  return (
                    <button
                      key={integration.id}
                      className={`${Styles.integrationButton} ${isSelected ? Styles.selected : ''}`}
                      onClick={() => setSelectedIntegrationId(integration.id)}
                    >
                      <div className={Styles.profileIcon}>
                        <img src={info.avatar || Silhouette} alt={info.name} />
                        <div
                          className={`${Styles.platformBadge} ${
                            integration.provider === ExternalIntegrationEnum.Instagram
                              ? Styles.instagram
                              : Styles.facebook
                          }`}
                        >
                          {integration.provider === ExternalIntegrationEnum.Instagram ? (
                            <InstagramBrand />
                          ) : (
                            <FacebookBrand />
                          )}
                        </div>
                      </div>
                      <div className={Styles.statsTitle}>
                        <div className={Styles.titleTop}>{info.name}&apos;s</div>
                        <div className={Styles.titleBottom}>Followers</div>
                      </div>
                    </button>
                  )
                })}
              </div>
            ) : (
              profileInfo && (
                <>
                  <div className={Styles.profileIcon}>
                    <img src={profileInfo.avatar} alt={profileInfo.name} />
                    {primaryIntegration && (
                      <div
                        className={`${Styles.platformBadge} ${
                          primaryIntegration.provider === ExternalIntegrationEnum.Instagram
                            ? Styles.instagram
                            : Styles.facebook
                        }`}
                      >
                        {primaryIntegration.provider === ExternalIntegrationEnum.Instagram ? (
                          <InstagramBrand />
                        ) : (
                          <FacebookBrand />
                        )}
                      </div>
                    )}
                  </div>
                  <div className={Styles.statsTitle}>
                    <div className={Styles.titleTop}>{profileInfo.name}&apos;s</div>
                    <div className={Styles.titleBottom}>Followers</div>
                  </div>
                </>
              )
            )}
          </div>

          <div className={Styles.stat}>
            <div className={Styles.statLabelRow}>
              <div className={Styles.statIndicator} />
              <div className={Styles.statLabel}>Before Blaze</div>
            </div>
            <div className={Styles.statValues}>
              <div className={Styles.statValue}>{beforeValue.toLocaleString()}</div>
              <div className={`${Styles.statDelta} ${Styles.negative}`}>
                {beforeDelta >= 0 ? '+' : ''}
                {beforeDelta}%
              </div>
            </div>
          </div>

          <div className={Styles.stat}>
            <div className={Styles.statLabelRow}>
              <div className={Styles.statIndicatorActive} />
              <div className={Styles.statLabel}>After Blaze</div>
            </div>
            <div className={Styles.statValues}>
              <div className={Styles.statValue}>{projectedFutureValue.toLocaleString()}</div>
              <div className={`${Styles.statDelta} ${Styles.positive}`}>+{projectedDelta}%</div>
            </div>
          </div>
        </div>

        {showHoverTooltip && tooltipData && tooltipData.baselineFollowers && (
          <div
            className={Styles.hoverTooltip}
            style={{
              left: tooltipPosition.x,
              top: tooltipPosition.y,
            }}
          >
            <div className={Styles.tooltipStat}>
              <div className={Styles.tooltipHeader}>
                <span className={`${Styles.tooltipDot} ${Styles.tooltipDotAccent}`} />
                <span className={`${Styles.tooltipLabel} ${Styles.tooltipLabelAccent}`}>After Blaze</span>
              </div>
              <div className={Styles.tooltipNumbers}>
                <span className={Styles.tooltipValue}>{tooltipData.followers.toLocaleString()}</span>
                <span className={`${Styles.tooltipDelta} ${Styles.tooltipDeltaPositive}`}>
                  +{Math.round(((tooltipData.followers - afterValue) / afterValue) * 100)}%
                </span>
              </div>
            </div>

            <div className={Styles.tooltipStat}>
              <div className={Styles.tooltipHeader}>
                <span className={`${Styles.tooltipDot} ${Styles.tooltipDotNeutral}`} />
                <span className={Styles.tooltipLabel}>Before Blaze</span>
              </div>
              <div className={Styles.tooltipNumbers}>
                <span className={Styles.tooltipValue}>{tooltipData.baselineFollowers.toLocaleString()}</span>
                <span className={`${Styles.tooltipDelta} ${Styles.tooltipDeltaNegative}`}>
                  {Math.round(((tooltipData.baselineFollowers - afterValue) / afterValue) * 100) >= 0 ? '+' : ''}
                  {Math.round(((tooltipData.baselineFollowers - afterValue) / afterValue) * 100)}%
                </span>
              </div>
            </div>
          </div>
        )}
      </div>

      {availableIntegrations.length < 2 && availableIntegrations.length > 0 && (
        <div className={Styles.connectContainer}>
          <div className={Styles.connectNextText}>
            Now link your{' '}
            {availableIntegrations[0].provider === ExternalIntegrationEnum.Instagram ? 'Facebook' : 'Instagram'} to see
            how you're doing.
          </div>
          <InitiateOauthTrigger
            provider={
              availableIntegrations[0].provider === ExternalIntegrationEnum.Instagram
                ? ExternalIntegrationEnum.Facebook
                : ExternalIntegrationEnum.Instagram
            }
            popUp
            onInitiatedOauth={connect}
          >
            <StyledIntegrationButton
              provider={
                availableIntegrations[0].provider === ExternalIntegrationEnum.Instagram ? 'facebook' : 'instagram'
              }
            />
          </InitiateOauthTrigger>
        </div>
      )}

      {/* Debug container - to be removed soon */}
      {process.env.NODE_ENV === 'development' && (
        <div className={Styles.debugContainer}>
          <TacticIntegration tactic="instagram" index={1} onConnect={handleIntegrationConnect} />
          <TacticIntegration tactic="facebook" index={2} onConnect={handleIntegrationConnect} />
        </div>
      )}
    </div>
  )
}
